import React, { useEffect, useState } from "react";
import ModalCover from "./ModalCover";
import AddressCard from "../Cards/AddressCard";
import GradientBtn from "../WebsiteComponents/GradientBtn";
import GoogleMapComponent from "../GlobalComponents/GoogleMapComponent";
import AddressForm from "../Forms/AddressForm";
import { IAddressData } from "src/types/GlobalInterfaces";
import {
  getShippingAddresses,
  handleAddressSelect,
  setShippingAddresses,
} from "src/helpers/helperFunctions";
import { AppDispatch, RootState } from "src/Store/store";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentAddress } from "src/Store/cartSlice";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import NoItemUI from "../NoItemUI";
import NoItemAddressImg from "src/assets/images/not-data/no-address.png";

interface IProps {
  onClose: (address?: IAddressData | null) => void;
  activeAddress: IAddressData | null;
}

const ChangeAddressModal = ({ onClose, activeAddress }: IProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLogged } = useSelector((state: RootState) => state.appReducer);
  const { address } = useSelector((state: RootState) => state.cartSlice);
  const [showSelectModal, setShowSelectModal] = useState<boolean>(false);
  const [pickAddress, setPickAddress] = useState<any>(null);
  const [addresses, setAddresses] = useState<IAddressData[]>([]);
  const [isEditAddress, setIsEditAddress] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<IAddressData | null>();
  const [getLoader, setGetLoader] = useState<boolean>(false);
  const [selectLoader, setSelectLoader] = useState<boolean>(false);

  useEffect(() => {
    if (activeAddress) {
      setSelectedAddress(activeAddress);
    }
  }, [activeAddress]);

  const fetchAllAddresses = async () => {
    if (!isLogged) return;
    setGetLoader(true);
    const { res, err } = await httpRequest<{ data: IAddressData[] | null }>({
      path: "/customer-address/get-customer-addresss",
    });
    if (res) {
      setAddresses(res?.data || []);
    } else {
      toast(err, { type: "error" });
    }
    setGetLoader(false);
  };

  useEffect(() => {
    if (isLogged) {
      setSelectedAddress(address as IAddressData);
      fetchAllAddresses();
    } else {
      setShowSelectModal(true);
    }
  }, []);

  useEffect(() => {
    setSelectedAddress(address as IAddressData);
  }, [address]);

  return (
    <>
      {showSelectModal && !pickAddress ? (
        <ModalCover
          onClose={() => {
            if (isLogged) {
              setShowSelectModal(false);
            } else {
              onClose();
            }
          }}
          title="Select Location"
        >
          <GoogleMapComponent
            confirmBtnId="google-map-confirm-btn"
            inputId="google-map-input"
            onConfirm={(address) => {
              console.log("Address Select By Users ===> ", address);
              if (isLogged) {
                setPickAddress(address);
              } else {
                dispatch(setCurrentAddress(address?.fullAddress || ""));
                onClose();
              }
            }}
            mapContainerClass="max-h-[80vh]"
            containerStyle={{
              height: "100%",
              minHeight: "60vh",
            }}
          />
        </ModalCover>
      ) : pickAddress ? (
        <ModalCover onClose={() => setPickAddress(null)} title="Address Form">
          <div className="w-full max-h-[80vh] overflow-y-auto">
            <AddressForm
              data={pickAddress}
              onConfirm={(data: IAddressData) => {
                console.log("Addresses ===> ", data);
                setIsEditAddress(false);
                setPickAddress(null);
                setShowSelectModal(false);
                setAddresses((prev) => {
                  const index = prev.findIndex((item) => item._id === data._id);
                  if (index !== -1) {
                    const updatedAddresses = [...prev];
                    updatedAddresses[index] = data;
                    return updatedAddresses;
                  }
                  return [...prev, data];
                });
              }}
              isEdit={isEditAddress}
              onEditSuccess={() => {
                setIsEditAddress(false);
                setPickAddress(null);
                setShowSelectModal(false);
                fetchAllAddresses();
              }}
              className="max-h-[60vh]"
            />
          </div>
        </ModalCover>
      ) : (
        <ModalCover
          onClose={() => onClose(selectedAddress)}
          title="Available Addresses"
        >
          <div className="w-full max-h-[60vh] overflow-y-auto flex flex-col gap-4">
            {getLoader ? (
              [1, 2].map((_, index) => {
                return (
                  <AddressCard
                    key={index}
                    skeleton
                    address={{} as IAddressData}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    onSelect={() => {}}
                  />
                );
              })
            ) : addresses.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center my-5">
                <NoItemUI
                  text="No addresses found"
                  text2="Add a new address to continue"
                  iconImagePath={NoItemAddressImg}
                  onButtonClick={() => {
                    setPickAddress(null);
                    setShowSelectModal(true);
                  }}
                  ButtonText="Add New Address"
                  buttonId="add-new-address-btn"
                />
              </div>
            ) : (
              addresses.map((address, index) => (
                <AddressCard
                  id={`address-card-${index + 1}`}
                  index={index}
                  editId={`address-card-edit-${index + 1}`}
                  deleteId={`address-card-delete-${index + 1}`}
                  key={address._id}
                  address={address}
                  onEdit={() => {
                    setPickAddress(address);
                    setIsEditAddress(true);
                  }}
                  onSelect={() => {
                    setSelectedAddress(address);
                  }}
                  onDelete={() => {}}
                  allAddresses={addresses}
                  setAllAddresses={setAddresses}
                  isActive={selectedAddress?._id === address._id}
                />
              ))
            )}
          </div>
          {addresses.length > 0 && (
            <div className="w-full grid grid-cols-2 gap-4 res-padd-top">
              <GradientBtn
                id="select-confirm-btn"
                fontsizeBtn="font-size-18px"
                fontfamilyBtn="font-Lexend-Medium"
                ButtonText="Select & Confirm"
                onPress={async () => {
                  setSelectLoader(true);
                  if (!selectedAddress?._id) {
                    toast.error("Please select an address");
                    setSelectLoader(false);
                    return;
                  }
                  let isSuccess = await handleAddressSelect(
                    selectedAddress?._id || ""
                  );
                  if (isSuccess) {
                    dispatch(
                      setCurrentAddress(selectedAddress as IAddressData)
                    );
                    onClose(selectedAddress as IAddressData);
                  } else {
                    toast.error("Please select an address");
                  }
                  setSelectLoader(false);
                }}
                disabled={selectLoader}
                loader={selectLoader}
              />
              <GradientBtn
                id="add-new-address-btn"
                fontsizeBtn="font-size-18px"
                fontfamilyBtn="font-Lexend-Medium"
                ButtonText="Add New Address"
                onPress={() => {
                  setPickAddress(null);
                  setShowSelectModal(true);
                }}
                isOutline
              />
            </div>
          )}
        </ModalCover>
      )}
    </>
  );
};

export default ChangeAddressModal;
